import Cover from "@/containers/Episode/partials/Cover";
import { cx, pluralize } from "@/utils";
import { SENTIMENT, SENTIMENT_OPTIONS } from "@/utils/sentimentOptions";
import { truncate } from "@/utils/truncate";
import { SourceDisplay } from "@components/DataDisplay";
import Table from "@components/Table";
import Tooltip from "@components/Tooltip";
import Typography from "@components/Typography";

import { BaseWidget } from "./BaseWidget";
import { sumArray } from "./helpers";

import styles from "./TopChannelsWidget.module.css";

const hasData = (data) =>
  data && data.map((d) => (d.topChannels || []).length).reduce(sumArray, 0) > 0;

const TopChannelsGeneralWidget = (props) => {
  const useColumns = props.size === 12;
  // hack: when not full width (using columns) limit to
  // 3 channels since pdfs are not scrollable
  const limit = props.limit || (useColumns ? 10 : 5);

  const channels = hasData(props.data)
    ? props.data
        .reduce((all, each) => {
          all.push(...each.topChannels);
          return all;
        }, [])
        .sort((a, b) => b.count - a.count)
        .slice(0, limit)
    : [];

  return (
    <BaseWidget {...props} hasData={hasData(props.data)}>
      {hasData(props.data) ? (
        <ol className={`${useColumns ? styles.useColumns : ""} w-full`}>
          {channels.map((channel, i) => (
            <li key={i} className={styles.listItem}>
              <div
                className={styles.channelColumn}
                onClick={() => {
                  const sources = props.data.map((d) => ({
                    id: d.meta.id,
                    type: d.meta.type,
                  }));

                  props.fetchMentions(sources, { channel_name: channel.name });
                }}
              >
                <Cover
                  className={styles.cover}
                  dumb
                  episode={{
                    ...channel,
                    source: channel.channel_type,
                    cover: channel.artwork,
                  }}
                />
                <div>
                  <Typography margin="none">
                    <strong>
                      {channel.name || L.REPORTS_WIDGET_UNKNOWN_SOURCE_NAME}
                    </strong>
                  </Typography>
                  <Typography
                    className={cx(styles.count, "text-muted-foreground")}
                  >
                    {channel.count}{" "}
                    {pluralize(
                      channel.count,
                      L.REPORTS_TOP_CHANNEL_WIDGET_MENTION_SINGULAR,
                      L.REPORTS_TOP_CHANNEL_WIDGET_MENTION_PLURAL
                    )}
                    {channel.reach ? (
                      <>
                        <br />
                        {L.REPORTS_TOP_CHANNEL_WIDGET_REACH_PER_WEEK}{" "}
                        {channel.reach}
                      </>
                    ) : (
                      ""
                    )}
                  </Typography>
                </div>
              </div>
            </li>
          ))}
        </ol>
      ) : (
        `${L.REPORTS_TOP_CHANNEL_WIDGET_LOADING}`
      )}
    </BaseWidget>
  );
};

const SentimentHeaderIcon = SENTIMENT.positive.icon;

const SentimentTooltip = ({ sentiment }) => (
  <>
    {SENTIMENT_OPTIONS.map((s) => {
      return (
        <Typography variant="body-2" margin="none" block key={s.label}>
          {s.pluralLabel}: {sentiment[s.value]}
        </Typography>
      );
    })}
  </>
);

const TopChannelsTableWidget = (props) => {
  return (
    <BaseWidget
      {...props}
      hasData={props.data && !!props.data[0]?.data.length}
      title={L.REPORTS_WIDGET_DEFAULT_TITLE_INSIGHTS_TOP_CHANNELS_TABLE}
      description={
        L.REPORTS_WIDGET_DEFAULT_TITLE_INSIGHTS_TOP_CHANNELS_TABLE_SUBTITLE
      }
    >
      <Table tight>
        <thead>
          <tr>
            <th colSpan={2}>{L.REPORTS_WIDGET_REACH_TABLE_SOURCE}</th>
            <th>{L.REPORTS_WIDGET_REACH_TABLE_MENTIONS}</th>
            <th>{L.REPORTS_WIDGET_REACH_TABLE_MEDIA}</th>
            <th>
              <SentimentHeaderIcon width={18} height={18} />
            </th>
          </tr>
        </thead>

        <tbody>
          {props.data
            ? props.data[0]?.data?.map((row, i) => {
                const sortedSentiments = Object.entries(
                  row.sentiment || {}
                ).sort((a, b) => b[1] - a[1]);
                const [sentimentName, _sentimentValue] = sortedSentiments[0];
                const SentimentIcon = SENTIMENT[sentimentName]?.icon;

                return (
                  <tr
                    key={i}
                    className={styles.clickableRow}
                    onClick={() =>
                      props.fetchMentions_v2({
                        channel_name: row.channel_name,
                      })
                    }
                  >
                    <td>
                      <Cover
                        className={styles.cover}
                        dumb
                        episode={{ source: row.channel_type, cover: row.cover }}
                      />
                    </td>
                    <td>
                      <strong>{truncate(row.channel_name, 50)}</strong>
                      <br />
                      {row.reach && row.reach !== 0 ? (
                        <>
                          {L.REPORTS_WIDGET_REACH_TABLE_REACH}:{" "}
                          {row.reach.toLocaleString()}
                        </>
                      ) : (
                        ""
                      )}
                    </td>
                    <td>{row.count.toLocaleString()}</td>
                    <td>
                      <strong>
                        <SourceDisplay id={row.channel_type} />
                      </strong>
                    </td>
                    <td
                      className={cx({
                        [styles.positive]: sentimentName === "positive",
                        [styles.very_positive]:
                          sentimentName === "very_positive",
                        [styles.negative]: sentimentName === "negative",
                        [styles.very_negative]:
                          sentimentName === "very_negative",
                      })}
                    >
                      <Tooltip
                        asChild={false}
                        content={<SentimentTooltip sentiment={row.sentiment} />}
                      >
                        <SentimentIcon width={18} height={18} />
                      </Tooltip>
                    </td>
                  </tr>
                );
              })
            : null}
        </tbody>
      </Table>
    </BaseWidget>
  );
};

const TopChannelsWidget = (props) => {
  return props.chartType == "table" ? (
    <TopChannelsTableWidget {...props} tight />
  ) : (
    <TopChannelsGeneralWidget {...props} />
  );
};

export { TopChannelsWidget };
